import React, { useEffect, useState } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  SelectChangeEvent,
  Box,
} from '@mui/material';
import {
  MenuItem as LegacyMenuItem,
  TextField as LegacyTextField,
} from '@material-ui/core';
import { useTenants } from './hooks/useTenants';
import { useAuthContext } from 'contexts/authContext';

interface TenantSelectorProps {
  value: number | string; // Carrier ID
  onChange: (value: number) => void;
  label?: string;
  includeAllOption?: boolean;
  disabled?: boolean;
  /**
   * Small outlined fields with the label in the border, to sit in a row of
   * other filters. Off by default, so screens already using this keep their look.
   */
  compact?: boolean;
}

export const TenantSelector = ({
  value,
  onChange,
  label = 'Tenant',
  includeAllOption = true,
  disabled = false,
  compact = false,
}: TenantSelectorProps) => {
  const { tenants, loading, error } = useTenants();
  const { user } = useAuthContext();
  const [selectedTenantId, setSelectedTenantId] = useState<number>(0);

  useEffect(() => {
    if (tenants && value) {
      // Find tenant that has this carrier
      const carrierId = Number(value);
      if (carrierId === 0) {
        setSelectedTenantId(0);
        return;
      }
      const tenant = tenants.tenants.find((t) =>
        t.carriers.some((c) => c.id === carrierId)
      );
      if (tenant) {
        setSelectedTenantId(tenant.id);
      }
    }
  }, [value, tenants]);

  const handleTenantChange = (event: SelectChangeEvent<unknown>) => {
    const newTenantId = Number(event.target.value);
    setSelectedTenantId(newTenantId);
    // When tenant changes, reset carrier to 0 ("All" if supported) or first carrier
    // Since backend requires carrierId for filtering, if user selects "All tenants" -> carrierId=0
    // If user selects specific tenant -> we should probably wait for carrier selection or auto-select first?
    if (newTenantId === 0) {
      onChange(0);
    } else {
      // Find first carrier of this tenant
      const tenant = tenants?.tenants.find((t) => t.id === newTenantId);
      if (tenant && tenant.carriers.length > 0) {
        onChange(tenant.carriers[0].id);
      } else {
        onChange(0); // No carriers?
      }
    }
  };

  const handleCarrierChange = (event: SelectChangeEvent<unknown>) => {
    onChange(Number(event.target.value));
  };

  if (loading) {
    return <CircularProgress size={20} />;
  }

  if (error) {
    return <Typography color="error">Error loading tenants</Typography>;
  }

  const { role } = user || { role: '' };

  if (role !== 'super_user') {
    return null;
  }

  const selectedTenant = tenants?.tenants.find((t) => t.id === selectedTenantId);
  const carriers = selectedTenant?.carriers || [];

  if (compact) {
    // Built from the same @material-ui/core fields as the filter rows it sits
    // in. The @mui/material Select below renders at a different size there,
    // because those pages carry the older library's global input styles.
    const fieldProps = {
      select: true,
      variant: 'outlined' as const,
      size: 'small' as const,
      disabled,
      InputLabelProps: { shrink: true },
      style: { minWidth: 160, backgroundColor: 'white' },
    };
    return (
      <Box display="flex" gap={1.5} alignItems="center">
        <LegacyTextField
          {...fieldProps}
          id="tenant-select"
          label={label || 'Tenant'}
          value={selectedTenantId}
          onChange={(e) => handleTenantChange(e as unknown as SelectChangeEvent<unknown>)}
        >
          {includeAllOption && <LegacyMenuItem value={0}>All tenants</LegacyMenuItem>}
          {tenants?.tenants.map((tenant) => (
            <LegacyMenuItem key={tenant.id} value={tenant.id}>
              {tenant.name}
            </LegacyMenuItem>
          ))}
        </LegacyTextField>

        {selectedTenantId !== 0 && (
          <LegacyTextField
            {...fieldProps}
            id="carrier-select"
            label="Carrier"
            value={value}
            onChange={(e) => handleCarrierChange(e as unknown as SelectChangeEvent<unknown>)}
          >
            {carriers.map((carrier) => (
              <LegacyMenuItem key={carrier.id} value={carrier.id}>
                {carrier.name}
              </LegacyMenuItem>
            ))}
          </LegacyTextField>
        )}
      </Box>
    );
  }

  return (
    <Box display="flex" gap={2} alignItems="center">
      <FormControl variant="outlined" style={{ minWidth: 120 }}>
        {label && (
          <Typography
            variant="body1"
            style={{ marginRight: 16, color: 'black', display: 'inline-block' }}
          >
            {label}
          </Typography>
        )}
        <Select
          value={selectedTenantId}
          onChange={handleTenantChange}
          style={{ color: 'black', height: 40 }}
          disabled={disabled}
        >
          {includeAllOption && (
            <MenuItem value={0} style={{ color: 'black' }}>
              All
            </MenuItem>
          )}
          {tenants?.tenants.map((tenant) => (
            <MenuItem
              key={tenant.id}
              value={tenant.id}
              style={{ color: 'black' }}
            >
              {tenant.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedTenantId !== 0 && (
        <FormControl variant="outlined" style={{ minWidth: 120 }}>
          <Typography
            variant="body1"
            style={{ marginRight: 16, color: 'black', display: 'inline-block' }}
          >
            Carrier
          </Typography>
          <Select
            value={value}
            onChange={handleCarrierChange}
            style={{ color: 'black', height: 40 }}
            disabled={disabled}
          >
            {carriers.map((carrier) => (
              <MenuItem
                key={carrier.id}
                value={carrier.id}
                style={{ color: 'black' }}
              >
                {carrier.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

export default TenantSelector;
